import { useMemo } from 'react';
import { css } from '@emotion/react';
import { OpeningRequest, ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { Provider, Workstation } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import dayjs from 'dayjs';
import type { AppointmentType } from '@frontend/api-appointment-types';
import { DataSourcesTypes } from '@frontend/api-data-sources';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { isEmpty } from '@frontend/validation';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Chip,
  ContentLoader,
  DatePickerField,
  DropdownField,
  FormRow,
  MessageTemplate,
  NakedButton,
  TagType,
  Text,
  TimeField,
  UsePopoverDialogResponse,
  numeric,
} from '@frontend/design-system';
import { defaultTemplate } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/constant';
import { useManageScheduleRequestModalForm } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useManageScheduleRequestModalForm';
import {
  ReducedAppointmentType,
  ReducedPractitioner,
} from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useReviewRequest';
import { SourceTenantListItem } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestModal/types';
import { ScheduleRequestsTrackingIds } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/trackingIds';
import {
  getCustomFieldData,
  getDurationInMinutes,
} from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/utils';
import { ReadOnlyField } from '../ReadOnlyReviewAppointmentRequestForm/ReadOnlyField';
import { AppointmentStatusMappingBanner } from './appointment-status-mapping-banner';
import { WorkstationDropdownSearchList } from './workstation-dropdown-search-list';

type ReviewAppointmentRequestFormProps = {
  appointmentTypes: AppointmentType[] | ReducedAppointmentType[];
  isLoading: boolean;
  patientName: string;
  patientPhone: string;
  personDialogProps: UsePopoverDialogResponse<HTMLButtonElement, false, HTMLDialogElement>;
  disabledForm: boolean;
  getFieldProps: ReturnType<typeof useManageScheduleRequestModalForm>['scheduleRequestApprovalForm']['getFieldProps'];
  values: ReturnType<typeof useManageScheduleRequestModalForm>['scheduleRequestApprovalForm']['values'];
  dateField: ReturnType<typeof useManageScheduleRequestModalForm>['customDateField'];
  startTimeField: ReturnType<typeof useManageScheduleRequestModalForm>['customStartTimeField'];
  endTimeField: ReturnType<typeof useManageScheduleRequestModalForm>['customEndTimeField'];
  isValidCustomEndTime: boolean;
  selectedScheduleRequest: ScheduleRequest | undefined;
  showDataSourceDropdown?: boolean;
  showSourceLocationDropdown?: boolean;
  syncAppDataSources?: DataSourcesTypes.DataSource[];
  providerList: Provider[] | ReducedPractitioner[];
  workstationList: { available: Workstation[]; all: Workstation[] };
  appointmentWritebackSourceTenants?: SourceTenantListItem[];
  showWorkstationDropdown: boolean;
  showGroupedWorkstations: boolean;
  isLoadingAvailableWorkstations: boolean;
  onTemplateChange: (template: string) => void;
  template: string;
  templateTags: TagType[];
  isReadOnlyTemplate: boolean;
  isPaymentsEnabled?: boolean;
  paidAmount?: number;
  customFieldMetaData?: string;
};

export const ReviewAppointmentRequestFormV2 = ({
  isLoading,
  patientName,
  patientPhone,
  personDialogProps,
  disabledForm,
  getFieldProps,
  appointmentTypes,
  selectedScheduleRequest,
  values,
  dateField,
  startTimeField,
  endTimeField,
  isValidCustomEndTime,
  showDataSourceDropdown,
  showSourceLocationDropdown,
  syncAppDataSources,
  providerList,
  workstationList,
  appointmentWritebackSourceTenants,
  showWorkstationDropdown,
  showGroupedWorkstations,
  isLoadingAvailableWorkstations,
  onTemplateChange,
  template,
  templateTags,
  isReadOnlyTemplate,
  isPaymentsEnabled,
  paidAmount,
  customFieldMetaData,
}: ReviewAppointmentRequestFormProps) => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const customFieldData = getCustomFieldData(customFieldMetaData);

  const selectedSource = useMemo(() => {
    const sourceTenant = appointmentWritebackSourceTenants?.find(
      ({ sourceTenantId }) => sourceTenantId === values.source
    );

    return {
      sourceId: sourceTenant?.sourceId,
      locationName: sourceTenant?.name,
      pmsName: sourceTenant?.pmsName,
    };
  }, [appointmentWritebackSourceTenants, values.source]);

  return (
    <>
      <section css={sectionStyle}>
        <ContentLoader show={isLoading} />
        <div css={headerStyles}>
          <Avatar name={patientName} />
          <section>
            <Text weight='bold' size='large'>
              {patientName}
            </Text>
            <Text size='small' color='light'>
              {patientPhone}
            </Text>
          </section>
          <NakedButton
            trackingId={ScheduleRequestsTrackingIds.linkPatientBtn}
            css={linkPatientStyles}
            {...personDialogProps.getTriggerProps()}
          >
            <Icon color='primary' name='user-small' />
            <Text as='span' color='primary' weight='bold'>
              {t('Link Patient')}
            </Text>
          </NakedButton>
        </div>
        <form css={{ marginTop: theme.spacing(3) }}>
          <FormRow cols={[100]}>
            <DropdownField disabled={disabledForm} label={t('Appointment Type')} {...getFieldProps('appointmentType')}>
              {appointmentTypes?.map((item) => (
                <DropdownField.Option key={item?.id} value={item?.id}>
                  {`${item.name} (${t('{{count}} mins', { count: getDurationInMinutes(item.durationMinutes) })} )`}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FormRow>
          <FormRow cols={[100]}>
            <DropdownField disabled={disabledForm} label={t('Date & Time')} {...getFieldProps('dateAndTime')}>
              {selectedScheduleRequest?.requestedOpenings?.map((item: OpeningRequest) => (
                <DropdownField.Option value={item?.dateTime ?? ''} key={item?.dateTime ?? ''}>
                  {dayjs(item.dateTime).format('MMM DD @ hh:mm a')}
                </DropdownField.Option>
              ))}
              <DropdownField.Option value='custom'>Custom</DropdownField.Option>
            </DropdownField>
          </FormRow>
          {values.dateAndTime === 'custom' && (
            <section css={{ marginBottom: theme.spacing(3) }}>
              <FormRow cols={[100]}>
                <DatePickerField name='customDate' label='Custom Date' css={{ maxWidth: '100%' }} {...dateField} />
              </FormRow>
              <FormRow cols={[50, 50]} css={{ marginBottom: theme.spacing(0.5) }}>
                <TimeField
                  name='customStartTime'
                  label='Custom Start Time'
                  showAdornment
                  css={fieldStyles}
                  {...startTimeField}
                />
                <TimeField
                  name='customEndTime'
                  label='Custom End Time'
                  showAdornment
                  css={fieldStyles}
                  {...endTimeField}
                />
              </FormRow>
              {!isValidCustomEndTime && (
                <Text size='small' color='error' textAlign='right'>
                  {t('End time must be after start time')}
                </Text>
              )}
            </section>
          )}
          <FormRow cols={[50, 50]}>
            {showSourceLocationDropdown && (
              <DropdownField
                css={[[fieldStyles, sourceTextStyles]]}
                disabled={disabledForm}
                label={t('Location')}
                {...getFieldProps('source')}
              >
                {appointmentWritebackSourceTenants?.map(({ sourceTenantId = '', name, pmsName }) => (
                  <DropdownField.Option css={sourceTextStyles} key={sourceTenantId} value={sourceTenantId}>
                    <span className='text'>{name}</span>
                    <span className='small-text'>{pmsName}</span>
                  </DropdownField.Option>
                ))}
              </DropdownField>
            )}
            {showDataSourceDropdown && (
              <DropdownField
                css={[fieldStyles, sourceTextStyles]}
                disabled={disabledForm}
                label={t('Select Source')}
                {...getFieldProps('source')}
              >
                {syncAppDataSources?.map(({ SourceID, SourceName, PracticeManagementSystem }) => (
                  <DropdownField.Option css={sourceTextStyles} key={SourceID} value={SourceID}>
                    <span className='text'>{SourceName}</span>
                    <span className='small-text'>{PracticeManagementSystem}</span>
                  </DropdownField.Option>
                ))}
              </DropdownField>
            )}
            <DropdownField
              css={fieldStyles}
              disabled={disabledForm}
              label={t('Select Provider')}
              {...getFieldProps('provider')}
            >
              {providerList.map(({ id, firstName, lastName }) => (
                <DropdownField.Option value={id ?? ''} key={id}>
                  {firstName} {lastName}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </FormRow>
          <AppointmentStatusMappingBanner
            locationId={selectedScheduleRequest?.locationId}
            sourceId={selectedSource.sourceId}
            locationName={selectedSource.locationName}
            pmsName={selectedSource.pmsName}
          />
          {showWorkstationDropdown && (
            <FormRow>
              <WorkstationDropdownSearchList
                workstationList={workstationList}
                fieldProps={getFieldProps('workstation')}
                disabled={disabledForm}
                showGroupedWorkstations={showGroupedWorkstations}
                isLoadingAvailableWorkstations={isLoadingAvailableWorkstations}
              />
            </FormRow>
          )}
          <FormRow cols={[100]} css={{ marginTop: theme.spacing(-1) }}>
            {isPaymentsEnabled && (
              <ReadOnlyField
                label={t('Paid')}
                value={
                  !!paidAmount ? (
                    <Chip variant='warn' leftElement={<Icon name='dollar-sign-small' />}>
                      {numeric(paidAmount, { decimals: 2 })}
                    </Chip>
                  ) : (
                    <>-</>
                  )
                }
              />
            )}
          </FormRow>
          <div
            css={{
              display: 'grid',
              gap: theme.spacing(1),
              gridTemplateColumns: '50% 50%',
              marginBottom: theme.spacing(3),
            }}
          >
            {!isEmpty(customFieldData) &&
              Object.entries(customFieldData).map(([key, value]) => (
                <ReadOnlyField key={key} label={key} value={value} />
              ))}
          </div>
        </form>
        <MessageTemplate
          initialTemplate={template || defaultTemplate}
          tags={templateTags}
          draggableTags={true}
          onChange={onTemplateChange}
          readOnly={isReadOnlyTemplate}
        >
          <MessageTemplate.Editor />
          <Text weight='light' color='light'>
            {t('You can also drag and drop, or click a tag below, to add your message')}
          </Text>
          <MessageTemplate.TagList />
        </MessageTemplate>
      </section>
    </>
  );
};

const sectionStyle = css`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.small};
  padding: ${theme.spacing(2)};
`;

const headerStyles = css`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${theme.colors.neutral10};
  border-top: 1px solid ${theme.colors.neutral10};
  padding: ${theme.spacing(2)};

  & > section {
    margin-left: ${theme.spacing(2)};
  }
`;

const linkPatientStyles = css`
  display: flex;
  align-items: center;
  width: 130px;
  margin-left: auto;
  cursor: pointer;

  & > svg {
    margin-right: ${theme.spacing(0.5)};
  }
`;

const fieldStyles = css`
  width: 200px;
`;

const sourceTextStyles = css`
  & .text {
    margin-right: ${theme.spacing(1)};
  }

  & .small-text {
    font-size: ${theme.font.size.small};
    line-height: ${theme.font.lineHeight};
    color: ${theme.font.colors.subdued};
  }
`;
